// Utility functions to map passenger counts to Amadeus API format

export interface AmadeusPassenger {
    id: string;
    travelerType: "ADULT" | "CHILD" | "HELD_INFANT";
    fareOptions?: string[];
}

/**
 * Converts passenger counts to Amadeus API format
 * @param adults - Number of adults (12+ years)
 * @param children - Number of children (2-11 years)
 * @param infants - Number of infants (0-23 months)
 * @returns Array of passenger objects for Amadeus API
 */
export const formatPassengersForAmadeus = (
    adults: number,
    children: number,
    infants: number
): AmadeusPassenger[] => {
    const passengers: AmadeusPassenger[] = [];
    let passengerId = 1;

    // Add adults
    for (let i = 0; i < adults; i++) {
        passengers.push({
            id: passengerId.toString(),
            travelerType: "ADULT",
        });
        passengerId++;
    }

    // Add children
    for (let i = 0; i < children; i++) {
        passengers.push({
            id: passengerId.toString(),
            travelerType: "CHILD",
        });
        passengerId++;
    }

    // Add infants (lap infants in Amadeus terms)
    for (let i = 0; i < infants; i++) {
        passengers.push({
            id: passengerId.toString(),
            travelerType: "HELD_INFANT",
        });
        passengerId++;
    }

    return passengers;
};

/**
 * Gets passenger count query parameters for Amadeus search API
 * @param adults - Number of adults
 * @param children - Number of children
 * @param infants - Number of infants
 * @returns Object with query parameters for passenger counts
 */
export const getPassengerQueryParams = (
    adults: number,
    children: number,
    infants: number
) => {
    const params: Record<string, number> = {
        adults: adults,
    };

    if (children > 0) {
        params.children = children;
    }

    if (infants > 0) {
        params.infants = infants;
    }

    return params;
};

/**
 * Formats passenger display text for UI
 * @param adults - Number of adults
 * @param children - Number of children
 * @param infants - Number of infants
 * @returns Formatted string for display
 */
export const formatPassengerDisplay = (
    adults: number,
    children: number,
    infants: number
): string => {
    const total = adults + children + infants;

    // If only adults, show "X Adult(s)"
    if (children === 0 && infants === 0) {
        if (adults === 1) {
            return "1 Adult";
        }
        return `${adults} Adults`;
    }

    // If mixed passenger types, show "X Passenger(s)"
    if (total === 1) {
        return "1 Passenger";
    }
    return `${total} Passengers`;
};
